import React, { useState, useEffect, useRef, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Decimation
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import crosshairPlugin from "chartjs-plugin-crosshair";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Decimation,
    zoomPlugin,
    crosshairPlugin
);

const PRICE_COLOR = 'green';
const HIGHLIGHT_COLOR = 'rgba(255, 0, 0, 0.7)';
const X_RANGE = [-50, 50];  // rv_iv (bps)
const Y_RANGE = [-1, 1];    // vol_trend (normalized)
const K_FRACTION = 0.0078125; // 0.78125% of data

const HeatmapNeighborsTimeSeriesChart = ({ chartHeight = '400px' }) => {
    const [data, setData] = useState([]);
    const [timestamp, setTimestamp] = useState("");
    const [loading, setLoading] = useState(true);
    const chartRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL || "";

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_URL}/data/volatility`);
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

                const { data: jsonData, timestamp: fileTimestamp } = await response.json();
                setData(jsonData);
                setTimestamp(fileTimestamp);
            } catch (error) {
                console.error("Error fetching volatility data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [API_URL]);

    // Compute KNN from full volatility dataset — same algorithm as heatmap explorer
    const similarIndices = useMemo(() => {
        if (data.length === 0) return new Set();

        // Build valid training points (have both rv_iv and vol_trend)
        const validPoints = [];
        for (let i = 0; i < data.length; i++) {
            if (data[i].rv_iv != null && data[i].vol_trend != null) {
                validPoints.push({ idx: i, x: data[i].rv_iv, y: data[i].vol_trend });
            }
        }

        const latestRow = data[data.length - 1];
        const queryX = latestRow?.rv_iv;
        const queryY = latestRow?.vol_trend;

        if (queryX == null || queryY == null || validPoints.length === 0) return new Set();

        // Normalize query to [0, 1]
        const cxN = (queryX - X_RANGE[0]) / (X_RANGE[1] - X_RANGE[0]);
        const cyN = (queryY - Y_RANGE[0]) / (Y_RANGE[1] - Y_RANGE[0]);

        // Compute Euclidean distances (excluding latest point)
        const pointsWithDist = [];
        for (const p of validPoints) {
            if (p.idx === data.length - 1) continue;
            const dx = (p.x - X_RANGE[0]) / (X_RANGE[1] - X_RANGE[0]) - cxN;
            const dy = (p.y - Y_RANGE[0]) / (Y_RANGE[1] - Y_RANGE[0]) - cyN;
            pointsWithDist.push({ idx: p.idx, dist: Math.sqrt(dx * dx + dy * dy) });
        }

        // Sort by distance and take K nearest
        pointsWithDist.sort((a, b) => a.dist - b.dist);
        const K = Math.max(1, Math.floor(validPoints.length * K_FRACTION));

        const result = new Set();
        for (let j = 0; j < Math.min(K, pointsWithDist.length); j++) {
            result.add(pointsWithDist[j].idx);
        }
        return result;
    }, [data]);

    const chartConfig = useMemo(() => {
        if (data.length === 0) return null;

        const labels = data.map(d => d.date);
        const closeData = data.map((d, i) => ({ x: i, y: d.close }));
        const highlightData = data.map((d, i) =>
            similarIndices.has(i) ? { x: i, y: d.close } : { x: i, y: null }
        );

        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        const twoYearsAgoStr = twoYearsAgo.toISOString().split('T')[0];
        const startYear2022Idx = data.findIndex(d => d.date >= twoYearsAgoStr);
        const initialMinIdx = startYear2022Idx >= 0 ? startYear2022Idx : 0;

        return { labels, closeData, highlightData, initialMinIdx, neighborCount: similarIndices.size };
    }, [data, similarIndices]);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
    }

    if (!chartConfig) {
        return <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>;
    }

    const { labels, closeData, highlightData, initialMinIdx, neighborCount } = chartConfig;

    const chartData = {
        labels: labels,
        datasets: [
            {
                label: "S&P 500 Close",
                data: closeData,
                borderColor: PRICE_COLOR,
                backgroundColor: PRICE_COLOR,
                borderWidth: 1.5,
                tension: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 2,
            },
            {
                label: `Heatmap Neighbors (${neighborCount})`,
                data: highlightData,
                borderColor: HIGHLIGHT_COLOR,
                backgroundColor: HIGHLIGHT_COLOR,
                borderWidth: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 4,
                pointStyle: 'circle',
                showLine: false,
                spanGaps: false,
                order: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        normalized: true,
        plugins: {
            decimation: {
                enabled: true,
                algorithm: 'lttb',
                samples: 500,
            },
            legend: {
                position: "top",
                labels: {
                    font: { size: window.innerWidth < 600 ? 10 : 12 },
                    usePointStyle: true,
                },
            },
            title: {
                display: false,
            },
            tooltip: {
                mode: "index",
                intersect: false,
                position: "nearest",
                callbacks: {
                    title: (items) => {
                        if (items.length > 0) {
                            const idx = Math.round(items[0].raw?.x ?? items[0].dataIndex);
                            if (idx >= 0 && idx < labels.length) {
                                return labels[idx];
                            }
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.raw?.y ?? context.raw;
                        if (value === null || value === undefined) return null;
                        if (context.dataset.label === "S&P 500 Close") {
                            return `Close: $${value.toFixed(2)}`;
                        } else if (context.dataset.label.startsWith("Heatmap")) {
                            const idx = Math.round(context.raw?.x ?? context.dataIndex);
                            const row = data[idx];
                            const rviv = row?.rv_iv != null ? row.rv_iv.toFixed(0) : 'n/a';
                            const vt = row?.vol_trend != null ? row.vol_trend.toFixed(2) : 'n/a';
                            return `Neighbor (RV-IV: ${rviv} bps, Trend: ${vt})`;
                        }
                        return `${context.dataset.label}: ${value}`;
                    },
                    filter: (tooltipItem) => {
                        const val = tooltipItem.raw?.y ?? tooltipItem.raw;
                        return val !== null && val !== undefined;
                    },
                },
            },
            zoom: {
                pan: { enabled: true, mode: "x" },
                zoom: {
                    wheel: { enabled: true, speed: 0.09 },
                    pinch: { enabled: true, speed: 0.09 },
                    mode: "x"
                },
            },
            crosshair: {
                line: { color: "rgba(0, 0, 0, 0.2)", width: 1 },
                sync: { enabled: false },
                zoom: { enabled: false },
            },
        },
        interaction: { mode: "nearest", intersect: false, axis: "x" },
        scales: {
            x: {
                type: 'linear',
                min: initialMinIdx,
                max: data.length - 1,
                title: { display: true, text: "Date" },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10,
                    font: { size: 11 },
                    callback: function(value) {
                        const idx = Math.round(value);
                        if (idx >= 0 && idx < labels.length) {
                            return labels[idx];
                        }
                        return '';
                    }
                },
            },
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "S&P 500 Close ($)" },
                grid: { drawOnChartArea: true, color: "rgba(200, 200, 200, 0.2)" },
            },
        },
    };

    const resetZoom = () => {
        if (chartRef.current) {
            chartRef.current.resetZoom();
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ height: chartHeight }}>
                <Line ref={chartRef} data={chartData} options={options} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', padding: '8px 0' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    Updated: {timestamp}
                </span>
                <button
                    onClick={resetZoom}
                    style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        backgroundColor: '#4a90d9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Reset Zoom
                </button>
            </div>
        </div>
    );
};

export default HeatmapNeighborsTimeSeriesChart;
